import React, { useCallback } from 'react';
import { useIsMutating } from '@tanstack/react-query';
import { Table, TableBody, TableCell, TableRow } from '@/components/ui/Table';
import Spinner from '@/components/common/Spinner';
import Text from '@/components/ui/Text';
import Icon from '@/components/ui/Icon';
import { Button, LinkButton, UnstyledButton } from '@/components/ui/Buttons';
import I18n from '@/modules/i18n';
import cn from '@/utils/cn';
import CandidateRow from './CandidateRow';
import { CANDIDATE_COLUMN_COUNT } from './columns';
import BulkBarContent from '../BulkBarContent';
import SelectionAnnouncer from '../SelectionAnnouncer';
import EmptyState from '../EmptyState';
import FilterBar from '../FilterBar';
import useAddCorrelation, { candidateMutationKey } from '../hooks/useAddCorrelation';
import useCandidateFilters from '../hooks/useCandidateFilters';
import useCandidateFocus from '../hooks/useCandidateFocus';
import useCandidateSelection from '../hooks/useCandidateSelection';
import useCorrelationCandidates from '../hooks/useCorrelationCandidates';
import {
  CandidateStandard, CorrelationCategory, NO_FILTER, TagManagerVariant,
} from '../types';

const LIST_MAX_HEIGHT: Record<TagManagerVariant, string> = {
  card: 'tw-max-h-[230px]',
  inline: 'tw-max-h-[310px]',
  panel: 'tw-max-h-[30vh]',
};

interface AddCorrelationPanelProps {
  categories: CorrelationCategory[];
  initialCategory: string;
  modelId: number;
  modelType: string;
  panelId: string;
  /** The program whose counts a mutation should refresh. Absent on surfaces with no program. */
  programId?: number;
  variant: TagManagerVariant;
  onClose: () => void;
}

const AddCorrelationPanel = ({
  categories, initialCategory, modelId, modelType, panelId, programId, variant, onClose,
}: AddCorrelationPanelProps) => {
  const headingId = `${panelId}-heading`;
  const searchId = `${panelId}-search`;

  const {
    clearSearch, filters, gradeOptions, setFilter, showGrade, showState, stateOptions,
  } = useCandidateFilters({ categories, initialCategory });

  const {
    candidates, fetchNextPage, hasNextPage, isError, isFetching, isFetchingNextPage, isLoading,
    searchTerm, totalCount,
  } = useCorrelationCandidates({ enabled: true, filters, modelId, modelType });

  const {
    clearIntent, onFailure, panelRef, recordSearchIntent, recordSlotIntent, recordTagIntent, searchRef,
  } = useCandidateFocus({ candidates, isLoading });

  const { clearSelection, selected, selectedIds, toggleSelect } = useCandidateSelection({
    candidates, filters,
  });

  const { addCorrelation, addCorrelations } = useAddCorrelation({
    modelId,
    modelType,
    onBulkFailure: clearIntent,
    onBulkUndo: recordSearchIntent,
    onFailure,
    onUndo: recordTagIntent,
    programId,
  });

  const isAdding = useIsMutating({ mutationKey: candidateMutationKey(modelType, modelId) }) > 0;

  // Stable identity keeps React.memo on CandidateRow effective while the user types.
  const addCandidate = useCallback((candidate: CandidateStandard, slot: number) => {
    // The row leaves the list on success, so the same slot resolves to the next candidate's button.
    recordSlotIntent(slot);
    addCorrelation(candidate);
  }, [addCorrelation, recordSlotIntent]);

  // Recorded before the request, like the table's batch: by the time it lands the bar and its button
  // are gone, so there is nothing left to return focus to but the search field.
  const addSelected = () => {
    recordSearchIntent();
    addCorrelations(selected);
  };

  const clearSelectionAndRefocus = () => {
    clearSelection();
    searchRef.current?.focus();
  };

  const clearSearchAndRefocus = () => {
    clearSearch();
    searchRef.current?.focus();
  };

  // The same options as the table's, so the panel can sit on whatever it is filtered to.
  const categoryOptions = [
    { label: I18n.t('all_categories'), value: NO_FILTER },
    ...categories.map(({ label, value }) => ({ label, value })),
  ];

  return (
    <div
      ref={panelRef}
      aria-labelledby={headingId}
      className={cn(
        'tw-m-3 tw-bg-light-grey tw-rounded-lg',
        'tw-border tw-border-solid tw-border-grey',
      )}
      id={panelId}
      role="group"
    >
      <div className="tw-flex tw-items-baseline tw-gap-3 tw-px-3 tw-pt-3">
        <Text className="tw-text-tci-black" id={headingId} tag="h3" weight="bold">
          {I18n.t('add_correlation_heading')}
        </Text>
        <UnstyledButton className="tw-text-tci-purple tw-underline" onClick={onClose}>
          <Text size="sm" tag="span">{I18n.t('close')}</Text>
        </UnstyledButton>
      </div>

      <FilterBar
        categoryOptions={categoryOptions}
        filters={filters}
        gradeOptions={gradeOptions}
        searchId={searchId}
        searchRef={searchRef}
        showGrade={showGrade}
        showState={showState}
        stateOptions={stateOptions}
        onFilterChange={setFilter}
      />

      <div className={cn(
        'tw-mx-3 tw-mb-3 tw-overflow-hidden tw-bg-white tw-rounded-lg',
        'tw-border tw-border-solid tw-border-grey',
      )}
      >
        <SelectionAnnouncer count={selected.length} />

        {selected.length > 0 && (
          <div className="tw-px-4 tw-py-2 tw-border-b tw-border-solid tw-border-light-grey">
            <BulkBarContent
              actionLabel={I18n.t('add_n_selected', { count: selected.length })}
              count={selected.length}
              disabled={isAdding}
              onAction={addSelected}
              onClear={clearSelectionAndRefocus}
            />
          </div>
        )}

        {/* Dimmed rather than replaced while a search settles: those rows are still real standards,
            and blinking the list on every keystroke is worse than showing it going stale. */}
        <div
          aria-busy={isFetching}
          className={cn(
            'tw-overflow-y-auto',
            LIST_MAX_HEIGHT[variant],
            isFetching && !isLoading && !isFetchingNextPage && 'tw-opacity-60',
          )}
        >
          {isLoading && (
            <div className="tw-flex tw-items-center tw-gap-2 tw-py-6 tw-px-4 tw-text-sm tw-text-dark-grey">
              <Spinner /> {I18n.t('loading')}
            </div>
          )}

          {/* retry is 0 on the shared QueryClient, so one failed request is terminal. */}
          {isError && (
            <Text className="tw-text-very-dark-grey tw-py-6 tw-px-4" size="sm">
              {I18n.t('standards_load_error')}
            </Text>
          )}

          {!isLoading && !isError && (
            <>
              <Table className="tw-w-full tw-table-fixed tw-border-collapse">
                <TableBody>
                  {candidates.map((candidate, slot) => (
                    <CandidateRow
                      key={candidate.tag_id}
                      candidate={candidate}
                      disabled={isAdding}
                      isSelected={selectedIds.has(candidate.tag_id)}
                      slot={slot}
                      onAdd={addCandidate}
                      onToggleSelect={toggleSelect}
                    />
                  ))}
                  {hasNextPage && (
                    <TableRow>
                      <TableCell className="tw-text-center" colSpan={CANDIDATE_COLUMN_COUNT}>
                        <LinkButton
                          className="tw-inline-flex tw-items-center tw-gap-1 tw-text-tci-purple"
                          disabled={isFetchingNextPage}
                          onClick={() => fetchNextPage()}
                        >
                          <Text size="sm" tag="span" weight="semibold">
                            {I18n.t('show_more_remaining', { count: totalCount - candidates.length })}
                          </Text>
                          <Icon aria-hidden icon="ChevronDown" size={16} />
                        </LinkButton>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>

              {candidates.length === 0 && (searchTerm ? (
                <EmptyState
                  action={(
                    <Button outlineStyle small onClick={clearSearchAndRefocus}>{I18n.t('clear_search')}</Button>
                  )}
                  title={I18n.t('no_standards_match_your_search')}
                />
              ) : (
                <EmptyState
                  body={I18n.t('everything_already_correlated_body')}
                  title={I18n.t('everything_already_correlated')}
                />
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddCorrelationPanel;
